import { VoicefillLogo } from '@/assets/voicefill-logo';
import { cn } from '@/lib/utils';
import { Loader2, XIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';

interface Props {
  isTranscribing?: boolean;
  isSaving?: boolean;
  isLoading?: boolean;
  transcriptionExists?: boolean;
  startTranscription: () => Promise<void>;
  stopTranscription: () => Promise<void>;
}

export const RecordingButton = ({
  isTranscribing = false,
  isSaving = false,
  isLoading = false,
  transcriptionExists = false,
  startTranscription,
  stopTranscription,
}: Props) => {
  const { t } = useTranslation();

  const buttonState = useMemo(() => {
    if (isTranscribing) {
      return {
        label: t('home:stop'),
        variant: 'destructive' as const,
        onClick: stopTranscription,
        icon: <XIcon className="mr-2 h-4 w-4" />,
      };
    }
    if (isSaving) {
      return {
        label: t('home:saving'),
        variant: 'secondary' as const,
        onClick: () => {},
        icon: <Loader2 className="mr-2 h-4 w-4 animate-spin" />,
      };
    }
    if (transcriptionExists) {
      return {
        label: t('home:resume'),
        variant: 'secondary' as const,
        onClick: startTranscription,
        icon: (
          <span className="mr-2">
            <VoicefillLogo bottomBarColor="black" iconColor="black" />
          </span>
        ),
      };
    }
    return {
      label: t('home:start'),
      variant: 'default' as const,
      onClick: startTranscription,
      icon: (
        <span className="mr-2">
          <VoicefillLogo />
        </span>
      ),
    };
  }, [
    isTranscribing,
    isSaving,
    transcriptionExists,
    t,
    startTranscription,
    stopTranscription,
  ]);

  return (
    <Button
      className={cn('w-full', isTranscribing && 'animate-pulse')}
      onClick={buttonState.onClick}
      disabled={isLoading || isSaving}
      variant={buttonState.variant}>
      {buttonState.icon}
      {buttonState.label}
    </Button>
  );
};
